import AccentButton from "@components/ui/AccentButton";
import { Input } from "@components/ui/input";
import { useAppContext } from "@contexts/AppContext";
import {
	Box,
	Button,
	Container,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	makeStyles,
	Paper,
	Radio,
	RadioGroup,
	Typography,
} from "@material-ui/core";
import useCartStore, { SERVICE_TYPES } from "@stores/cartStore";
import useLanguageStore from "@stores/languageStore";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import SEOHead from "../../components/SEOHead";

const useStyles = makeStyles(() => ({
	radio: {
		color: "#063447 !important",
		"&.Mui-checked": {
			color: "#063447 !important",
		},
	},
	formControlLabel: {
		marginLeft: "0 !important",
		marginRight: "0 !important",
	},
}));

const ScheduleRabiPage = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { language } = useLanguageStore();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const editItemId = searchParams.get("edit");
	const isRtl = language === "he";
	const addItem = useCartStore((state) => state.addItem);
	const updateItem = useCartStore((state) => state.updateItem);
	const getItemById = useCartStore((state) => state.getItemById);
	const { showSuccess } = useAppContext();

	const [date, setDate] = useState("");
	const [time, setTime] = useState("");
	const [nusach, setNusach] = useState("ashkenazi");
	const [firstName, setFirstName] = useState("");

	// Load cart item data if in edit mode
	useEffect(() => {
		if (editItemId) {
			const item = getItemById(editItemId);
			if (item && item.serviceType === SERVICE_TYPES.SCHEDULE_RABBI) {
				if (item.data.date) setDate(item.data.date);
				if (item.data.time) setTime(item.data.time);
				if (item.data.nusach) setNusach(item.data.nusach);
				if (item.data.firstName) setFirstName(item.data.firstName);
			}
		}
	}, [editItemId, getItemById]);

	const handleSubmit = (e) => {
		e.preventDefault();
		// Handle form submission here
		const [hour, minute] = time ? time.split(":") : ["", ""];
		console.log({ date, hour, minute, nusach, firstName });
	};

	return (
		<div
			className={`min-h-[calc(100vh-64px)] py-20 sm:py-32 max-w-[100vw] overflow-x-hidden ${
				isRtl ? "rtl" : "ltr"
			}`}
		>
			<Container maxWidth="md" className="px-4 sm:px-8">
				<SEOHead
					title={t("scheduleRabi.title")}
					description={t("scheduleRabi.subtitle")}
				/>
				<div className="text-center mb-12">
					<Typography
						variant="h3"
						component="h1"
						className="mb-4 !font-bold text-black"
					>
						{t("scheduleRabi.title")}
					</Typography>
					<Typography
						variant="body1"
						component="p"
						className="text-stone-600 font-normal"
					>
						{t("scheduleRabi.subtitle")}
					</Typography>
				</div>

				<Paper className="p-8 bg-white shadow-lg" elevation={3}>
					<form onSubmit={handleSubmit}>
						<Grid container spacing={4}>
							<Grid item xs={12} sm={6}>
								<Input
									label={t("scheduleRabi.form.date")}
									type="date"
									value={date}
									onChange={(e) => setDate(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
									className={isRtl ? "!text-right !justify-end" : undefined}
									required
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<Input
									label={t("scheduleRabi.form.time")}
									type="time"
									value={time}
									onChange={(e) => setTime(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
									className={isRtl ? "!text-right !justify-end" : undefined}
									required
								/>
							</Grid>

							<Grid item xs={12}>
								<FormControl component="fieldset" fullWidth>
									<FormLabel
										component="legend"
										style={{
											marginBottom: "12px",
											textAlign: isRtl ? "right" : "left",
										}}
									>
										{t("scheduleRabi.form.nusach")}
									</FormLabel>
									<RadioGroup
										value={nusach}
										onChange={(e) => setNusach(e.target.value)}
										row={!isRtl}
										style={{
											flexDirection: isRtl ? "row-reverse" : "row",
											justifyContent: isRtl ? "flex-end" : "flex-start",
											columnGap: "24px",
										}}
									>
										<FormControlLabel
											value="ashkenazi"
											control={<Radio className={classes.radio} />}
											label={t("scheduleRabi.form.nusachAshkenazi")}
											className={classes.formControlLabel}
										/>
										<FormControlLabel
											value="sephardi"
											control={<Radio className={classes.radio} />}
											label={t("scheduleRabi.form.nusachSephardi")}
											className={classes.formControlLabel}
										/>
									</RadioGroup>
								</FormControl>
							</Grid>

							<Grid item xs={12}>
								<Input
									label={t("scheduleRabi.form.firstName")}
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									dir={isRtl ? "rtl" : "ltr"}
									required
								/>
							</Grid>

							<Grid item xs={12}>
								<Box className="flex gap-3">
									<Button
										type="submit"
										variant="contained"
										size="large"
										className="flex-1 px-8 py-2 normal-case font-semibold bg-stone-700 hover:bg-stone-600"
									>
										{t("scheduleRabi.form.submit")}
									</Button>
									<AccentButton
										onClick={() => {
											if (!date || !firstName || !time) {
												return;
											}
											const [hour, minute] = time.split(":");
											const itemData = {
												date,
												hour,
												minute,
												time,
												nusach,
												firstName,
											};

											if (editItemId) {
												// Update existing item
												updateItem(editItemId, itemData);
												showSuccess(t("scheduleRabi.form.updatedInCart"));
												// Navigate back to cart after a short delay
												setTimeout(() => {
													navigate("/cart");
												}, 1000);
											} else {
												// Add new item
												addItem({
													serviceType: SERVICE_TYPES.SCHEDULE_RABBI,
													data: itemData,
												});
												showSuccess(t("scheduleRabi.form.addedToCart"));
											}
										}}
										disabled={!date || !firstName || !time}
										className="flex-1"
									>
										{editItemId
											? t("scheduleRabi.form.updateCart")
											: t("scheduleRabi.form.addToCart")}
									</AccentButton>
								</Box>
							</Grid>
						</Grid>
					</form>
				</Paper>
			</Container>
		</div>
	);
};

export default ScheduleRabiPage;
